// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { ProductInfo } from "@insite/client-framework/Common/ProductInfo";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import getLocalizedDateTime from "@insite/client-framework/Common/Utilities/getLocalizedDateTime";
import { ProductContext } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import updateProductInfo from "@insite/client-framework/Store/Components/ProductInfoList/Handlers/UpdateProductInfo";
import { getProductInfoFromList } from "@insite/client-framework/Store/Components/ProductInfoList/ProductInfoListSelectors";
import translate from "@insite/client-framework/Translate";
import { AutocompleteItemModel, ProductModel } from "@insite/client-framework/Types/ApiModels";
import ProductPrice, { ProductPriceStyles } from "@insite/content-library/Components/ProductPrice";
import ProductQuantityOrdered from "@insite/content-library/Components/ProductQuantityOrdered";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import { TextFieldPresentationProps } from "@insite/mobius/TextField";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { getProp } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { LazyImageProps } from "@mcmc/MobiusOverrides/LazyImage";
import ProductAddToCartButton from "@mcmc/Overrides/Components/ProductAddToCartButton";
import ProductPartNumbers, { ProductPartNumbersStyles } from "@mcmc/Overrides/Components/ProductPartNumbers";
import CloudinaryImage from "@mcmc/XDL/Components/Common/CloudinaryImage";
import MultilineEllipsis from "@mcmc/XDL/Components/Common/MultiLineEllipsis";
import { screen } from "@mcmc/XDL/Helpers";
import { Roboto, RobotoCondensed } from "@mcmc/XDL/Utilities";
import { transformationsMap } from "@mcmc/XDL/Utilities/cloudinary";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState, ownProps: OwnProps) => ({
    language: state.context.session.language,
    productInfo:
        ownProps.productInfo ||
        (ownProps.productInfoListId
            ? getProductInfoFromList(state, ownProps.productInfoListId, ownProps.product.id)
            : null),
});

const mapDispatchToProps = {
    updateProductInfo,
};

export interface AutocompleteProductTileModel extends AutocompleteItemModel {
    productInfoListId?: string | null;
    product: ProductModel;
    productInfo?: ProductInfo | null;
    onQtyOrderedChanged?: (qty: number) => void;
}

type OwnProps = AutocompleteProductTileModel & {
    isEvent: boolean;
    goToUrl: (url: string) => void;
    disableAddToCart?: boolean;
};

type Props = OwnProps & ResolveThunks<typeof mapDispatchToProps> & ReturnType<typeof mapStateToProps>;

export interface ProductCarouselProductStyles {
    imageGridItem?: InjectableCss;
    image?: LazyImageProps;
    infoGridItem?: InjectableCss;
    titleLink?: LinkPresentationProps;
    priceActionWrapper?: InjectableCss;
    productPrice?: ProductPriceStyles;
    productActionsWrapper?: InjectableCss;
    extendedQtyFieldStyles?: TextFieldPresentationProps;
    extendedAddToCartStyles?: ButtonPresentationProps;
    eventDetailsText?: TypographyPresentationProps;
    productPartNumbersStyles?: ProductPartNumbersStyles;
}

const priceTextStyle: TypographyPresentationProps = {
    ...RobotoCondensed,
    size: 16,
    lineHeight: "19px",
    weight: "bold",
};

export const productCarouselProductStyles: ProductCarouselProductStyles = {
    imageGridItem: {
        css: css`
            flex: 0 0 100px;
            height: 90px;
            padding-right: 10px;
            min-width: 0;
        `,
    },
    image: {
        width: "90px",
    },
    infoGridItem: {
        css: css`
            display: flex;
            flex-direction: column;
            justify-content: space-between;
            flex: 1 1 calc(100% - 100px);
            min-width: 0;
        `,
    },
    titleLink: {
        color: "text",
        hoverStyle: {
            color: "text.main",
            textDecoration: "underline",
        },
        typographyProps: {
            ...Roboto,
            color: "text",
            size: 16,
            lineHeight: "20px",
            weight: "normal",
            css: css`
                margin: 5px 0 10px 0;
                letter-spacing: 0.2px;
                min-width: 0;
            `,
        },
    },
    productPartNumbersStyles: {
        container: {
            css: css`
                margin-bottom: 5px;
            `,
        },
    },
    priceActionWrapper: {
        css: css`
            display: flex;
            flex-wrap: wrap;
            justify-content: space-between;
            align-items: center;
        `,
    },
    productPrice: {
        wrapper: {
            css: css`
                min-height: 35px;
                display: flex;
                align-items: center;
            `,
        },
        price: {
            priceText: priceTextStyle,
            signInText: priceTextStyle,
            errorText: {
                ...priceTextStyle,
                weight: "normal",
            },
            realTimeText: priceTextStyle,
            unitOfMeasureText: priceTextStyle,
        },
        quoteMessage: { text: priceTextStyle },
    },
    productActionsWrapper: {
        css: css`
            display: flex;
            justify-content: flex-end;
            flex: 1 1 auto;
            padding-left: 10px;
        `,
    },
    extendedQtyFieldStyles: {
        sizeVariant: "small",
        cssOverrides: {
            formField: css`
                ${getProp("theme.formField.defaultProps.cssOverrides.formField")}
                width: 45px;
                margin-right: 10px;
            `,
        },
    },
    extendedAddToCartStyles: {
        sizeVariant: "medium",
        css: css`
            margin-left: 10px;
            min-width: 135px;
        `,
    },
    eventDetailsText: {
        variant: "p",
        size: 13,
        lineHeight: "15px",
        weight: "normal",
        css: css`
            margin-bottom: 0;
        `,
    },
};

const styles = productCarouselProductStyles;

const productActionsWrapperClickHandler = (e: any) => e.stopPropagation();

const AutocompleteProductTile: React.FC<Props> = ({
    productInfoListId,
    product,
    updateProductInfo,
    productInfo,
    onQtyOrderedChanged,
    isEvent,
    goToUrl,
    disableAddToCart,
    language,
}) => {
    let updateQuantity;
    if (productInfoListId) {
        updateQuantity = (qtyOrdered: number) => {
            updateProductInfo({ id: productInfoListId, productId: product.id, qtyOrdered });
        };
    } else {
        updateQuantity = (qtyOrdered: number) => {
            onQtyOrderedChanged?.(qtyOrdered);
        };
    }

    if (!product || !productInfo) {
        return null;
    }

    const productContext = {
        product,
        productInfo,
        onQtyOrderedChanged: updateQuantity,
    };

    let eventStartDate;
    let eventEndDate;
    let productLinkPrefix = "";
    let productLinkPostfix = "";
    if (isEvent) {
        eventStartDate =
            product.properties.eventStartDateFormatted &&
            getLocalizedDateTime({
                dateTime: new Date(product.properties.eventStartDateFormatted),
                language,
                options: { year: "2-digit", month: "2-digit", day: "2-digit" },
            });
        eventEndDate =
            product.properties.eventEndDateFormatted &&
            getLocalizedDateTime({
                dateTime: new Date(product.properties.eventEndDateFormatted),
                language,
                options: { year: "2-digit", month: "2-digit", day: "2-digit" },
            });
        productLinkPostfix = product.properties.eventCategory ? `(${product.properties.eventCategory})` : "";
    } else {
        productLinkPrefix = `${product.brand && product.brand.name ? `<strong>${product.brand.name}</strong>` : ""}`;
    }

    return (
        <ProductContext.Provider value={productContext}>
            {!isEvent && (
                <StyledWrapper {...styles.imageGridItem}>
                    <CloudinaryImage
                        {...styles.image}
                        imageTransformations={transformationsMap.product_thumbnil}
                        src={product.smallImagePath || product.mediumImagePath}
                        altText={product.productTitle}
                    />
                </StyledWrapper>
            )}
            <StyledWrapper {...styles.infoGridItem}>
                <Link
                    {...styles.titleLink}
                    onClick={(e: React.MouseEvent) => {
                        e.stopPropagation();
                        goToUrl(product.canonicalUrl);
                    }}
                >
                    <MultilineEllipsis
                        content={`${productLinkPrefix} ${product.productTitle} ${productLinkPostfix}`}
                        maxLines={2}
                        lineHeight={styles.titleLink?.typographyProps?.lineHeight}
                    />
                </Link>
                {!isEvent && (
                    <>
                        <ProductPartNumbers
                            manufacturerItem={product.manufacturerItem}
                            productNumber=""
                            extendedStyles={styles.productPartNumbersStyles}
                        />
                        <StyledWrapper {...styles.priceActionWrapper}>
                            <ProductPrice
                                showLabel={false}
                                product={productContext}
                                extendedStyles={styles.productPrice}
                            />
                            {!disableAddToCart && (
                                <StyledWrapper
                                    {...styles.productActionsWrapper}
                                    onClick={productActionsWrapperClickHandler}
                                >
                                    <ProductQuantityOrdered
                                        extendedStyles={styles.extendedQtyFieldStyles}
                                        labelOverride=""
                                        id={`${product.id}-qty`}
                                    />
                                    <ProductAddToCartButton
                                        iconOnly={true}
                                        extendedStyles={styles.extendedAddToCartStyles}
                                        data-test-selector={`actionsAddToCart${product.id}`}
                                    />
                                </StyledWrapper>
                            )}
                        </StyledWrapper>
                    </>
                )}
                {isEvent && (
                    <StyledWrapper {...styles.priceActionWrapper}>
                        <StyledWrapper>
                            <Typography {...styles.eventDetailsText}>{product.properties.eventLocation}</Typography>
                            <Typography {...styles.eventDetailsText}>
                                {eventStartDate} {eventStartDate && eventEndDate && "-"} {eventEndDate}
                            </Typography>
                        </StyledWrapper>
                        <StyledWrapper {...styles.productActionsWrapper}>
                            <ProductPrice
                                showLabel={false}
                                product={productContext}
                                extendedStyles={styles.productPrice}
                            />
                            <Button
                                {...styles.extendedAddToCartStyles}
                                onClick={(e: React.MouseEvent) => {
                                    e.stopPropagation();
                                    goToUrl(product.canonicalUrl);
                                }}
                            >
                                {translate("View Details")}
                            </Button>
                        </StyledWrapper>
                    </StyledWrapper>
                )}
            </StyledWrapper>
        </ProductContext.Provider>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(AutocompleteProductTile);
